import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { getAccessToken } from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Building2, Users2, Contact2, FileCheck2, Clock, CheckCircle2,
  AlertTriangle, FileText, ArrowLeft, Loader2, Download,
} from "lucide-react";

const STATUS_STYLE = {
  draft: { label: "Draft", color: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
  submitted: { label: "Awaiting Review", color: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  needs_revision: { label: "Needs Revision", color: "bg-rose-500/10 text-rose-500 border-rose-500/30" },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
};

export default function ProspectiveOnboardingReview() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: c }] = await Promise.all([
          API.get("/onboarding/me"),
          API.get("/onboarding-catalog/documents"),
        ]);
        setSub(s);
        setCatalog(c);
      } catch {
        toast.error("Failed to load submission");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (!sub) {
    return <p className="text-muted-foreground">No submission found.</p>;
  }

  const le = sub.legal_entity || {};
  const status = sub.status;
  const isReadOnly = status === "submitted" || status === "approved";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="onboarding-review-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Onboarding Submission</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Review your submission</h1>
          <div className="flex items-center gap-3 mt-3">
            <Badge variant="outline" className={`${STATUS_STYLE[status]?.color} border text-xs px-3 py-1`} data-testid="review-status-badge">
              {STATUS_STYLE[status]?.label || status}
            </Badge>
            {sub.submitted_at && (
              <span className="text-xs text-muted-foreground">Submitted {new Date(sub.submitted_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            )}
          </div>
        </div>
        {status === "needs_revision" && (
          <Button onClick={() => navigate("/onboarding")} data-testid="continue-edit-btn">
            Update Submission
          </Button>
        )}
      </div>

      {status === "submitted" && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-4 flex gap-3 items-start">
            <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-500 text-sm">We're working on your onboarding</p>
              <p className="text-sm text-muted-foreground mt-1">Our team is reviewing your submission. You'll be notified as soon as it's approved.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {status === "needs_revision" && (
        <Card className="border-rose-500/40 bg-rose-500/5">
          <CardContent className="py-4 flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5" />
            <div>
              <p className="font-medium text-rose-500 text-sm">Revisions requested</p>
              {sub.revision_notes && <p className="text-sm text-foreground mt-1 whitespace-pre-wrap leading-relaxed">{sub.revision_notes}</p>}
            </div>
          </CardContent>
        </Card>
      )}
      {status === "approved" && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="py-4 flex gap-3 items-start">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-emerald-500 text-sm">Onboarding approved</p>
              <p className="text-sm text-muted-foreground mt-1">Sign out and back in to access your fund management workspace.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legal Entity */}
      <Section icon={Building2} title="Legal Entity">
        <KV label="Legal Name" value={le.legal_name} />
        <KV label="Incorporation Number" value={le.incorporation_number} />
        <KV label="Country of Incorporation" value={le.country_of_incorporation} />
        <KV label="Company Email" value={le.company_email} />
        <KV label="Company Phone" value={le.company_phone} />
        <KV label="Company Website" value={le.company_website} />
        <KV label="Correspondence Address" value={le.correspondence_address} full />
      </Section>

      {/* Signatories */}
      <Section icon={Users2} title={`Authorized Signatories (${sub.signatories?.length || 0})`}>
        {(sub.signatories || []).map((s, i) => (
          <div key={i} className="md:col-span-2 rounded-lg border border-border bg-secondary/20 p-4">
            <p className="text-sm font-medium mb-3">Signatory {i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <KV label="Name" value={s.name} />
              <KV label="Title" value={s.title} />
              <KV label="Email" value={s.email} />
              <KV label="LinkedIn" value={s.linkedin || "—"} />
              <KV label="Phone" value={s.phone} />
              <KV label="Mobile" value={s.mobile} />
            </div>
          </div>
        ))}
      </Section>

      {/* Contacts */}
      <Section icon={Contact2} title={`Primary Point of Contact (${sub.contacts?.length || 0})`}>
        {(sub.contacts || []).map((c, i) => (
          <div key={i} className="md:col-span-2 rounded-lg border border-border bg-secondary/20 p-4">
            <p className="text-sm font-medium mb-3">Contact {i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <KV label="Name" value={c.name} />
              <KV label="Title" value={c.title} />
              <KV label="Email" value={c.email} />
              <KV label="LinkedIn" value={c.linkedin || "—"} />
              <KV label="Phone" value={c.phone} />
              <KV label="Mobile" value={c.mobile} />
            </div>
          </div>
        ))}
      </Section>

      {/* Documents */}
      <Section icon={FileCheck2} title="Documents">
        <div className="md:col-span-2 space-y-5">
          {catalog.map((section) => (
            <div key={section.section}>
              <p className="text-sm font-semibold mb-2">{section.section}</p>
              <div className="space-y-1.5">
                {section.items.map((item) => {
                  const doc = sub.documents?.[item.key];
                  return (
                    <div key={item.key} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2" data-testid={`review-doc-${item.key}`}>
                      <FileText className={`w-4 h-4 flex-shrink-0 ${doc ? "text-emerald-500" : "text-muted-foreground"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{item.label}</p>
                        {doc && <p className="text-xs text-muted-foreground truncate">{doc.file_name} • {(doc.size / 1024).toFixed(1)} KB</p>}
                      </div>
                      {doc ? (
                        <>
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 text-[10px]">Uploaded</Badge>
                          <DownloadButton submissionId={sub.id} docKey={item.key} fileName={doc.file_name} />
                        </>
                      ) : (
                        <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/30 text-[10px]">Missing</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {!isReadOnly && (
        <Button variant="outline" onClick={() => navigate("/onboarding")} data-testid="back-to-form-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Form
        </Button>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Icon className="w-5 h-5 text-primary" /> {title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function KV({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm mt-1 break-words">{value || <span className="text-muted-foreground/60">—</span>}</p>
    </div>
  );
}

function DownloadButton({ submissionId, docKey, fileName }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const token = getAccessToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/${submissionId}/documents/${docKey}`,
        { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `${docKey}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download document");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="ghost" size="sm" onClick={handle} disabled={busy} data-testid={`download-doc-${docKey}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
      Download
    </Button>
  );
}
